/**
 * The Cogni results table's filters (Region pills, Approval Year, Last Updated,
 * Refine chips) as pure predicates over a DrugResultV2, shared by
 * SearchResultsTable's TanStack column filters and the Compare page's Cogni
 * rail — so "the drugs the user filtered to" means the same thing in both
 * (ticket 2.4: "AB view should show the same set of drugs as what the user
 * filtered for in results view").
 *
 * The filter state lives in the search-page URL (SearchResultsTable's
 * updateUrlFilters); COGNI_TABLE_FILTER_PARAMS is that param set, which the
 * Compare hand-off copies onto the /compare-keyword URL verbatim.
 */
import type { DrugResultV2, SearchRegion } from '../types';
import { approvalYear } from './approvalDate';
import { regionDisplayLabel } from '../constants/filterConstants';
import { cogniRefineFields, formatRefineChipValue, matchesRefineChips, parseRefineParam, REFINE_KIND_LABELS, type RefineChip } from './refineQuery';

export const COGNI_RESULTS_REGION_PARAM = 'resultRegions';
export const COGNI_TABLE_FILTER_PARAMS = [
  COGNI_RESULTS_REGION_PARAM,
  'yearMin',
  'yearMax',
  'includeUnknown',
  'updatedStart',
  'updatedEnd',
  'includeUndated',
  'refine',
] as const;

export interface ApprovalYearRange {
  min: number | null;
  max: number | null;
  includeUnknown: boolean;
}

export interface LastUpdatedRange {
  start: string | null;
  end: string | null;
  includeUndated: boolean;
}

export interface CogniTableFilters {
  regions: SearchRegion[];
  approvalYear: ApprovalYearRange;
  lastUpdated: LastUpdatedRange;
  refine: RefineChip[];
}

/** Region pills: an empty selection passes everything; a missing region is US. */
export function matchesRegion(region: string | null | undefined, regions: SearchRegion[]): boolean {
  if (!regions || regions.length === 0) return true;
  return regions.includes((region || 'US') as SearchRegion);
}

/** Approval year range. No bound set → everything passes (includeUnknown only
 *  applies once a bound is set). */
export function matchesApprovalYear(dateStr: string | null | undefined, range: ApprovalYearRange): boolean {
  const { min, max, includeUnknown } = range;
  if (min === null && max === null) return true;
  const year = approvalYear(dateStr);
  if (year === null) return includeUnknown;
  if (min !== null && year < min) return false;
  if (max !== null && year > max) return false;
  return true;
}

// A row is "undated" for Last Updated purposes when effective_time is null
// OR fails to parse as a valid date (`new Date()` never throws on a
// malformed string; it yields an Invalid Date whose getTime() is NaN).
export function isUndatedEffectiveTime(effectiveTime: string | null | undefined): boolean {
  if (!effectiveTime) return true;
  return isNaN(new Date(effectiveTime).getTime());
}

/** Last Updated range. Undated rows follow includeUndated whether or not a
 *  range is set; with no range, dated rows pass. */
export function matchesLastUpdated(dateStr: string | null | undefined, range: LastUpdatedRange): boolean {
  const { start, end, includeUndated } = range;
  if (isUndatedEffectiveTime(dateStr)) return includeUndated;
  if (!start && !end) return true;
  const date = new Date(dateStr as string);
  if (start && date < new Date(start)) return false;
  if (end && date > new Date(end + 'T23:59:59')) return false;
  return true;
}

/** Read the table's filter state from URL params (same parsing as the table). */
export function parseCogniTableFilters(params: URLSearchParams): CogniTableFilters {
  const regions = params.get(COGNI_RESULTS_REGION_PARAM);
  const yearMin = params.get('yearMin');
  const yearMax = params.get('yearMax');
  const min = yearMin ? parseInt(yearMin, 10) : null;
  const max = yearMax ? parseInt(yearMax, 10) : null;
  return {
    regions: regions ? (regions.split(',').filter(Boolean) as SearchRegion[]) : [],
    approvalYear: {
      min: Number.isNaN(min) ? null : min,
      max: Number.isNaN(max) ? null : max,
      includeUnknown: params.get('includeUnknown') !== 'false',
    },
    lastUpdated: {
      start: params.get('updatedStart'),
      end: params.get('updatedEnd'),
      includeUndated: params.get('includeUndated') !== 'false',
    },
    refine: parseRefineParam(params.get('refine')),
  };
}

/** Whether every table filter passes `row`. The approval-year filter only
 *  applies while the Approval Date column is shown, as in the table. */
export function matchesCogniTableFilters(
  row: DrugResultV2,
  f: CogniTableFilters,
  includeApprovalDate: boolean,
): boolean {
  return matchesRegion(row.region, f.regions)
    && (!includeApprovalDate || matchesApprovalYear(row.approval_date, f.approvalYear))
    && matchesLastUpdated(row.effective_time, f.lastUpdated)
    && matchesRefineChips(cogniRefineFields(row, includeApprovalDate), f.refine);
}

/** The /compare-keyword URL for a Cogni hand-off. The table's filter params on
 *  the search page are copied over verbatim. */
export function buildCogniCompareUrl(searchParams: URLSearchParams): string {
  const out = new URLSearchParams({ src: 'cogni' });
  for (const k of COGNI_TABLE_FILTER_PARAMS) {
    const v = searchParams.get(k);
    if (v) out.set(k, v);
  }
  return `/compare-keyword?${out.toString()}`;
}

/** Human labels for the active filters (Compare's "Filtered:" note). Empty
 *  when nothing would narrow the rows. */
export function cogniTableFilterLabels(f: CogniTableFilters, includeApprovalDate: boolean, usFdaVisible = false): string[] {
  const out: string[] = [];
  if (f.regions.length > 0) out.push(`Region: ${f.regions.map((r) => regionDisplayLabel(r, usFdaVisible)).join(', ')}`);
  const y = f.approvalYear;
  if (includeApprovalDate && (y.min !== null || y.max !== null)) {
    out.push(`Approved: ${y.min ?? '…'}–${y.max ?? '…'}${y.includeUnknown ? '' : ' (dated only)'}`);
  }
  const u = f.lastUpdated;
  if (u.start || u.end) out.push(`Updated: ${u.start ?? '…'} – ${u.end ?? '…'}`);
  if (!u.includeUndated) out.push('Updated: dated only');
  for (const c of f.refine) out.push(`${REFINE_KIND_LABELS[c.kind]}: ${formatRefineChipValue(c, usFdaVisible)}`);
  return out;
}
